import { AlertTriangle, Calendar, Tag, FileSearch } from 'lucide-react'
import type { IOCMatch } from '../types'

interface IOCCardProps {
  ioc: IOCMatch
}

function getConfidenceColor(confidence: number): string {
  if (confidence >= 75) return 'text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700'
  if (confidence >= 50) return 'text-orange-700 dark:text-orange-300 bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-700'
  return 'text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700'
}

function getConfidenceLabel(confidence: number): string {
  if (confidence >= 75) return 'High Confidence'
  if (confidence >= 50) return 'Medium Confidence'
  return 'Low Confidence'
}

export default function IOCCard({ ioc }: IOCCardProps) {
  const confidenceColorClass = getConfidenceColor(ioc.confidence_level)
  
  return (
    <div className="p-5 border border-slate-200 dark:border-slate-600 rounded-xl bg-gradient-to-br from-white to-slate-50/50 dark:from-slate-800 dark:to-slate-900/50 shadow-sm hover:shadow-md transition-shadow duration-200">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <AlertTriangle className="h-5 w-5 text-orange-600 dark:text-orange-400" />
          <div>
            <h5 className="font-semibold text-gray-900 dark:text-white">
              Malicious Indicator Detected
            </h5>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              {ioc.threat_type_desc}
            </p>
          </div>
        </div>
        <div className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${confidenceColorClass}`}>
          {getConfidenceLabel(ioc.confidence_level)}
        </div>
      </div>

      <div className="space-y-4">
        <div className="bg-slate-50 dark:bg-slate-700 p-4 rounded-lg border border-slate-200 dark:border-slate-600">
          <div className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
            Indicator
          </div>
          <div className="font-mono text-sm text-orange-700 dark:text-orange-300 break-all bg-white dark:bg-slate-800 p-2 rounded border border-slate-200 dark:border-slate-600">
            {ioc.indicator}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-slate-50 dark:bg-slate-700 p-3 rounded-lg border border-slate-200 dark:border-slate-600">
            <div className="text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">
              Threat Type
            </div>
            <div className="text-sm text-slate-900 dark:text-slate-100 font-medium">
              {ioc.threat_type}
            </div>
          </div>
          
          <div className="bg-slate-50 dark:bg-slate-700 p-3 rounded-lg border border-slate-200 dark:border-slate-600">
            <div className="text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">
              Malware Family
            </div>
            <div className="text-sm text-slate-900 dark:text-slate-100 font-medium">
              {ioc.malware_printable || ioc.malware || 'Unknown'}
            </div>
          </div>
        </div>

        {ioc.tags.length > 0 && (
          <div className="bg-slate-50 dark:bg-slate-700 p-4 rounded-lg border border-slate-200 dark:border-slate-600">
            <div className="flex items-center space-x-2 text-xs font-semibold text-slate-600 dark:text-slate-400 mb-3">
              <Tag className="h-4 w-4" />
              <span>Threat Intelligence Tags</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {ioc.tags.map((tag, index) => (
                <span
                  key={index}
                  className="px-3 py-1.5 text-xs bg-orange-100 dark:bg-orange-900/40 text-orange-800 dark:text-orange-200 rounded-full font-medium border border-orange-200 dark:border-orange-700"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="flex items-center space-x-2 text-xs text-slate-600 dark:text-slate-400 bg-slate-50 dark:bg-slate-700 p-3 rounded-lg border border-slate-200 dark:border-slate-600">
            <Calendar className="h-4 w-4" />
            <span className="font-medium">First seen: {new Date(ioc.first_seen).toLocaleDateString()}</span>
          </div>
          {ioc.last_seen && (
            <div className="flex items-center space-x-2 text-xs text-slate-600 dark:text-slate-400 bg-slate-50 dark:bg-slate-700 p-3 rounded-lg border border-slate-200 dark:border-slate-600">
              <Calendar className="h-4 w-4" />
              <span className="font-medium">Last seen: {new Date(ioc.last_seen).toLocaleDateString()}</span>
            </div>
          )}
        </div>

        <div className="bg-slate-50 dark:bg-slate-700 p-4 rounded-lg border border-slate-200 dark:border-slate-600">
          <div className="flex items-center space-x-2 text-xs font-semibold text-slate-600 dark:text-slate-400 mb-3">
            <FileSearch className="h-4 w-4" />
            <span>Context in Log</span>
          </div>
          <div className="bg-white dark:bg-slate-800 p-3 rounded-lg text-xs font-mono text-slate-700 dark:text-slate-300 break-all border border-slate-200 dark:border-slate-600">
            {ioc.context}
          </div>
        </div>
      </div>
    </div>
  )
}
